import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useRepositoryMutation, useRepositoryQuery } from '@mews-commander/data-access';
import {
    LinkButton,
    LocalizedPageTitle,
    LocalizedParagraph,
    OptimusProvider,
    OverlayedLoader,
} from '@mews-commander/ui';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Link, Pictogram, Stack } from '@optimus-web/core';
import { getDeviceFingerprintPayload } from '../../../ducks/actions';
import { NewPublicFormWrapper } from '../../style';
import { useSignIn } from '../useSignIn';

export const WaitForDeviceApproval = () => {
    const navigate = useNavigate();
    const { signIn, isLoginPending } = useSignIn();
    const { data: trustedDeviceInfo, error } = useRepositoryQuery(
        'generalUsers',
        'getDeviceStatus',
        { device: getDeviceFingerprintPayload() },
        {
            refetchInterval: 5000,
        }
    );

    const { mutate: resendDeviceAuthEmail } = useRepositoryMutation('generalUsers', 'resendDeviceAuthEmail', {
        onSuccess: () => {
            showSuccessAlert({
                title: lt('EmailSentSuccessfully'),
                description: <LocalizedText textKey={TextKey.CheckYourEmail} />,
            });
        },
    });

    const resendAuthenticationLink = () => resendDeviceAuthEmail({ device: getDeviceFingerprintPayload() });

    useEffect(() => {
        // If the API call fails we can assume that the device is not logged in
        if (error) {
            void navigate({ to: '/Home/SignIn' });
        }
    }, [error, navigate]);

    useEffect(() => {
        if (!trustedDeviceInfo?.isTrusted) {
            return;
        }

        void signIn();
    }, [signIn, trustedDeviceInfo]);

    return (
        <NewPublicFormWrapper vertical>
            <OptimusProvider density="spacious">
                <Stack spacing="500" itemAlignment="start" vertical>
                    <OverlayedLoader show={isLoginPending} />
                    <Pictogram value="email" size="large" />
                    <Stack spacing="300" itemAlignment="start" vertical>
                        <Stack spacing="100" vertical>
                            <LocalizedPageTitle textKey={TextKey.HardDeviceApprovalTitle} />
                            <LocalizedParagraph textKey={TextKey.HardDeviceApprovalDescription} />
                            <Link onClick={resendAuthenticationLink}>
                                <LocalizedText textKey={TextKey.ResendEmail} />
                            </Link>
                        </Stack>
                        <LinkButton to="/Home/SignIn">
                            <LocalizedText textKey={TextKey.GoBack} />
                        </LinkButton>
                    </Stack>
                </Stack>
            </OptimusProvider>
        </NewPublicFormWrapper>
    );
};
